import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { RiskService } from '@/services/riskService';
import { ContextualPanel, ContextualPanelItem } from '@/components/common/ContextualPanel';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, LinkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import { RelatedActionsWidget } from '@/components/actions/RelatedActionsWidget';

interface RiskDetailViewProps {
  riskId: string;
  onClose: () => void;
  /** When true, opens directly in edit mode (e.g. when opened from Edit action) */
  initialEditMode?: boolean;
}

interface Risk {
  id: string;
  name: string;
  description: string;
  severity: string;
  probability: string;
  escalation_level: string;
  risk_status: string;
  current_owner_id: string;
  current_owner_name: string;
  due_date: string;
  created_at: string;
  updated_at: string;
  identified_by_name: string;
  category_name?: string | null;
  strategy_name: string;
  vision_name: string;
  roadmap_name: string;
  business_hierarchy_name: string;
  business_hierarchy_level: number;
  business_hierarchy_type: string;
  business_hierarchy_id: string;
  functional_hierarchy_name: string;
  functional_hierarchy_level: number;
  functional_hierarchy_type: string;
  functional_hierarchy_id: string;
  geographic_hierarchy_name: string;
  geographic_hierarchy_level: number;
  geographic_hierarchy_type: string;
  geographic_hierarchy_id: string;
  [key: string]: any; // Allow additional properties
}

interface Comment {
  id: string;
  comment_text: string;
  author_name: string;
  created_at: string;
}

interface Activity {
  id: string;
  action_type: string;
  performed_by_name: string;
  created_at: string;
  notes: string;
}

export const RiskDetailView: React.FC<RiskDetailViewProps> = ({ riskId, onClose }) => {
  const navigate = useNavigate();
  // Removed unused variables
  const [risk, setRisk] = useState<Risk | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [transformations, setTransformations] = useState<any[]>([]);
  const [transformationsLoading, setTransformationsLoading] = useState(false);
  const [activePanel, setActivePanel] = useState<'transformations' | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableTransformations, setAvailableTransformations] = useState<any[]>([]);
  const [linkModalLoading, setLinkModalLoading] = useState(false);
  const [selectedTransformationId, setSelectedTransformationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialLoadDone = useRef(false);
  const dueDateInputRef = useRef<HTMLInputElement>(null);
  // Refs hold latest values synchronously so save always uses current user input (avoids React batching race)
  const formValuesRef = useRef<{
    name?: string; description?: string; risk_status?: string; severity?: string; probability?: string;
    due_date?: string; risk_register_id?: string; risk_category_id?: string; escalation_level?: string; current_owner_id?: string;
  }>({});
  const [newComment, setNewComment] = useState('');
  // Removed unused assigning state
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedProbability, setSelectedProbability] = useState('');
  const [selectedDueDate, setSelectedDueDate] = useState('');
  const [selectedRegisterId, setSelectedRegisterId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedEscalationLevel, setSelectedEscalationLevel] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [registers, setRegisters] = useState<{ id: string; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; first_name: string; last_name: string }[]>([]);

  const riskService = new RiskService();

  const escalationLevelOptions = [
    { value: '', label: '— None —' },
    { value: 'VISION', label: 'Vision' },
    { value: 'STRATEGY', label: 'Strategy' },
    { value: 'PROGRAM', label: 'Program' },
    { value: 'PROJECT', label: 'Project' },
    { value: 'ORGANIZATION', label: 'Organization' },
    { value: 'FUNCTION', label: 'Function' },
    { value: 'GEOGRAPHY', label: 'Geography' },
    { value: 'PROCESS', label: 'Process' },
    { value: 'SYSTEM', label: 'System' }
  ];

  // Helper function to format hierarchy name with simple dashes
  const formatHierarchyName = (name: string, level: number, type: string) => {
    if (!name) return 'Not assigned';
    
    // Simple dashes for each level
    const dashes = '--'.repeat(Math.max(0, level - 1));
    return `${dashes}${name} (${type})`;
  };


  useEffect(() => {
    initialLoadDone.current = false;
    formValuesRef.current = {};
    loadRiskDetails();
    loadTransformations();
    loadRegisters();
    loadCategories();
    loadUsers();
  }, [riskId]);

  // Native 'input' event - most reliable for date picker selection (fires before React's onChange in some browsers)
  useEffect(() => {
    const el = dueDateInputRef.current;
    if (!el) return;
    const handler = (e: Event) => {
      const v = (e.target as HTMLInputElement).value || '';
      setSelectedDueDate(v);
      formValuesRef.current = {...formValuesRef.current, due_date: v};
    };
    el.addEventListener('input', handler, { capture: true });
    return () => el.removeEventListener('input', handler, { capture: true });
  }, [risk]);

  const loadRegisters = async () => {
    try {
      const response = await api.get('/generic/risk_registers');
      const raw = response.data?.data ?? response.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setRegisters(Array.isArray(list) ? list.map((r: any) => ({ id: r.id, name: r.name || r.id })) : []);
    } catch (error) {
      console.error('Error loading registers:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/generic/risk_categories');
      const raw = response.data?.data ?? response.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      setCategories(Array.isArray(list) ? list.map((c: any) => ({ id: c.id, name: c.name || c.id })) : []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/generic/users');
      const list = response.data?.data ?? response.data ?? [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRiskDetails = async () => {
    try {
      setLoading(true);
      const riskData = await riskService.getRiskById(riskId);
      if (riskData) {
        const rd = riskData as any;
        const due = rd.due_date ? rd.due_date.split('T')[0] : '';
        const cat = rd.category_name ? (rd.risk_category_id || '') : '';
        setRisk(rd);
        setSelectedStatus(rd.risk_status || '');
        setSelectedSeverity(rd.severity || '');
        setSelectedProbability(rd.probability || '');
        setSelectedDueDate(due);
        setSelectedRegisterId(rd.risk_register_id || '');
        setSelectedCategoryId(cat);
        setSelectedEscalationLevel(rd.escalation_level || '');
        setSelectedOwnerId(rd.current_owner_id || '');
        formValuesRef.current = {
          name: rd.name,
          description: rd.description || '',
          risk_status: rd.risk_status || '',
          severity: rd.severity || '',
          probability: rd.probability || '',
          due_date: due,
          risk_register_id: rd.risk_register_id || '',
          risk_category_id: cat,
          escalation_level: rd.escalation_level || '',
          current_owner_id: rd.current_owner_id || ''
        };
      }
      await loadComments();
      await loadActivities();
    } catch (error) {
      console.error('Error loading risk details:', error);
      toast.error('Failed to load risk details');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };


  const loadComments = async () => {
    try {
      const response = await api.get(`/risks/${riskId}/comments`);
      setComments(response.data.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadActivities = async () => {
    try {
      const response = await api.get(`/risks/${riskId}/activities`);
      setActivities(response.data.data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const loadTransformations = async () => {
    try {
      setTransformationsLoading(true);
      const response = await api.get(`/risks/${riskId}/transformations`);
      if (response.data.success) {
        setTransformations(response.data.data);
      }
    } catch (error) {
      console.error('Error loading transformations:', error);
      // Don't show error toast - transformations are optional
    } finally {
      setTransformationsLoading(false);
    }
  };

  const openLinkModal = async () => {
    setShowLinkModal(true);
    setSelectedTransformationId('');
    try {
      setLinkModalLoading(true);
      const response = await api.get('/generic/transformations');
      const raw = response.data?.data ?? response.data;
      const all = Array.isArray(raw) ? raw : (raw?.data ?? []);
      const linkedIds = new Set(transformations.map((t) => t.id));
      const available = Array.isArray(all) ? all.filter((t) => t.id && !linkedIds.has(t.id)) : [];
      setAvailableTransformations(available);
    } catch (error) {
      console.error('Error loading transformations:', error);
      toast.error('Failed to load transformations');
      setAvailableTransformations([]);
    } finally {
      setLinkModalLoading(false);
    }
  };

  const handleLinkTransformation = async () => {
    if (!selectedTransformationId) {
      toast.error('Please select a transformation');
      return;
    }
    try {
      await api.post(`/risks/${riskId}/transformations`, { transformation_id: selectedTransformationId });
      toast.success('Transformation linked successfully');
      setShowLinkModal(false);
      await loadTransformations();
    } catch (error: any) {
      const msg = error?.response?.data?.error ?? error?.message ?? 'Failed to link';
      toast.error(msg);
    }
  };

  const handleUnlinkTransformation = async (transformationId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Unlink this transformation from the risk?')) return;
    try {
      await api.delete(`/risks/${riskId}/transformations/${transformationId}`);
      toast.success('Transformation unlinked');
      await loadTransformations();
    } catch (error: any) {
      const msg = error?.response?.data?.error ?? error?.message ?? 'Failed to unlink';
      toast.error(msg);
    }
  };

  const saveRisk = async (overrides?: { due_date?: string | null }) => {
    if (!risk) return;

    try {
      setSaving(true);
      const fv = formValuesRef.current;
      const dueDateValue = overrides?.due_date !== undefined
        ? (overrides.due_date && String(overrides.due_date).trim() ? String(overrides.due_date).trim() : null)
        : (fv.due_date ?? selectedDueDate) && String(fv.due_date ?? selectedDueDate).trim()
          ? String(fv.due_date ?? selectedDueDate).trim()
          : null;
      const updateData: Record<string, unknown> = {
        name: fv.name ?? risk.name,
        description: (fv.description ?? risk.description) || '',
        risk_status: fv.risk_status ?? selectedStatus,
        severity: (fv.severity ?? selectedSeverity) as any,
        probability: (fv.probability ?? selectedProbability) as any,
        due_date: dueDateValue,
        risk_register_id: (fv.risk_register_id ?? selectedRegisterId) || null,
        risk_category_id: (fv.risk_category_id ?? selectedCategoryId) || null,
        escalation_level: (fv.escalation_level ?? selectedEscalationLevel) || null,
        current_owner_id: (fv.current_owner_id ?? selectedOwnerId) || null
      };

      await riskService.updateRisk(riskId, updateData as any);
      loadActivities();
      toast.success('Saved');
    } catch (error) {
      console.error('Error updating risk:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when editable fields change (debounced).
  useEffect(() => {
    if (!initialLoadDone.current || !risk) return;
    const timer = setTimeout(() => saveRisk(), 600);
    return () => clearTimeout(timer);
  }, [
    risk?.name,
    risk?.description,
    selectedStatus,
    selectedSeverity,
    selectedProbability,
    selectedDueDate,
    selectedRegisterId,
    selectedCategoryId,
    selectedEscalationLevel,
    selectedOwnerId
  ]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await api.post(`/risks/${riskId}/comments`, {
        comment_text: newComment
      });

      setNewComment('');
      await loadComments();
      await loadActivities();
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading risk details...</div>
      </div>
    );
  }

  if (!risk) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Risk not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {risk.name}
          {saving && <span className="ml-2 text-sm font-normal text-gray-500">Saving...</span>}
        </h1>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Risk Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risk Name</label>
              <Input
                value={risk.name}
                onChange={(e) => {
                  const v = e.target.value;
                  setRisk({...risk, name: v});
                  formValuesRef.current = {...formValuesRef.current, name: v};
                }}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea
                value={risk.description || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setRisk({...risk, description: v});
                  formValuesRef.current = {...formValuesRef.current, description: v};
                }}
                className="w-full"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select
                  value={selectedStatus}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedStatus(v);
                    formValuesRef.current = {...formValuesRef.current, risk_status: v};
                  }}
                  options={[
                    { value: 'IDENTIFIED', label: 'Identified' },
                    { value: 'ASSIGNED', label: 'Assigned' },
                    { value: 'ACCEPTED', label: 'Accepted' },
                    { value: 'DISMISSED', label: 'Dismissed' },
                    { value: 'ESCALATED', label: 'Escalated' },
                    { value: 'RESOLVED', label: 'Resolved' },
                    { value: 'CLOSED', label: 'Closed' }
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <Input
                  ref={dueDateInputRef}
                  type="date"
                  value={selectedDueDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedDueDate(v);
                    formValuesRef.current = {...formValuesRef.current, due_date: v};
                  }}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Probability</label>
                <Select
                  value={selectedProbability}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedProbability(v);
                    formValuesRef.current = {...formValuesRef.current, probability: v};
                  }}
                  options={[
                    { value: 'LOW', label: 'Low' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HIGH', label: 'High' }
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <Select
                  value={selectedSeverity}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedSeverity(v);
                    formValuesRef.current = {...formValuesRef.current, severity: v};
                  }}
                  options={[
                    { value: 'LOW', label: 'Low' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HIGH', label: 'High' },
                    { value: 'CRITICAL', label: 'Critical' }
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select
                  value={selectedCategoryId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedCategoryId(v);
                    formValuesRef.current = {...formValuesRef.current, risk_category_id: v};
                  }}
                  options={[
                    { value: '', label: '— No category —' },
                    ...categories.map(c => ({ value: c.id, label: c.name }))
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Register</label>
                <Select
                  value={selectedRegisterId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedRegisterId(v);
                    formValuesRef.current = {...formValuesRef.current, risk_register_id: v};
                  }}
                  options={[
                    { value: '', label: '— No register —' },
                    ...registers.map(r => ({ value: r.id, label: r.name }))
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                <Select
                  value={selectedEscalationLevel}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedEscalationLevel(v);
                    formValuesRef.current = {...formValuesRef.current, escalation_level: v};
                  }}
                  options={escalationLevelOptions}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                <Select
                  value={selectedOwnerId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedOwnerId(v);
                    formValuesRef.current = {...formValuesRef.current, current_owner_id: v};
                  }}
                  options={[
                    { value: '', label: '— Unassigned —' },
                    ...users.map(u => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id }))
                  ]}
                />
              </div>
            </div>

            {/* Hierarchy Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Hierarchy Information</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Hierarchy</label>
                  <div className="text-gray-900 font-mono text-sm whitespace-pre-line">
                    {formatHierarchyName(
                      risk.business_hierarchy_name, 
                      risk.business_hierarchy_level || 0, 
                      risk.business_hierarchy_type || ''
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Functional Hierarchy</label>
                  <div className="text-gray-900 font-mono text-sm whitespace-pre-line">
                    {formatHierarchyName(
                      risk.functional_hierarchy_name, 
                      risk.functional_hierarchy_level || 0, 
                      risk.functional_hierarchy_type || ''
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Geographic Hierarchy</label>
                  <div className="text-gray-900 font-mono text-sm whitespace-pre-line">
                    {formatHierarchyName(
                      risk.geographic_hierarchy_name, 
                      risk.geographic_hierarchy_level || 0, 
                      risk.geographic_hierarchy_type || ''
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Comments and Activities */}
        <div className="space-y-6">
          {/* Add Comment */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Add Comment</h2>
            <div className="space-y-3">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full"
                rows={3}
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Add Comment
              </Button>
            </div>
          </div>

          {/* Comments History */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Comments ({comments.length})</h2>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {comments.length === 0 ? (
                <div className="text-gray-500">No comments yet</div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="border-l-4 border-blue-200 pl-4">
                    <div className="text-sm text-gray-600">
                      {comment.author_name} • {new Date(comment.created_at).toLocaleString()}
                    </div>
                    <div className="text-gray-900 mt-1">{comment.comment_text}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Activity History */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Activity History ({activities.length})</h2>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {activities.length === 0 ? (
                <div className="text-gray-500">No activity yet</div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className="border-l-4 border-green-200 pl-4">
                    <div className="text-sm text-gray-600">
                      {activity.performed_by_name} • {new Date(activity.created_at).toLocaleString()}
                    </div>
                    <div className="text-gray-900 mt-1">
                      <span className="font-medium">{activity.action_type}</span>
                      {activity.notes && <span className="ml-2">{activity.notes}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Related Actions */}
          <RelatedActionsWidget
            contextType="risk"
            contextId={riskId}
            title="Related Actions"
          />

          {/* Related Transformations */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Related Transformations</h2>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setActivePanel('transformations')}
                >
                  View All ({transformations.length})
                </Button>
                <Button 
                  size="sm" 
                  onClick={openLinkModal}
                >
                  <LinkIcon className="w-4 h-4 mr-1" />
                  Link Transformation
                </Button>
              </div>
            </div>
            {transformationsLoading ? (
              <div className="text-center py-4 text-gray-500 text-sm">Loading transformations...</div>
            ) : transformations.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">No transformations linked to this risk</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {transformations.slice(0, 5).map((transformation) => (
                  <div
                    key={transformation.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/transformations/${transformation.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/transformations/${transformation.id}`)}
                    className="border border-gray-200 rounded p-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-900 text-xs truncate">{transformation.name}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded shrink-0 ${
                            transformation.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            transformation.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {transformation.status}
                          </span>
                        </div>
                        {transformation.description && (
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {transformation.description}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleUnlinkTransformation(transformation.id, e)}
                        className="ml-2 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Unlink"
                      >
                        Unlink
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contextual Panel - Related Transformations */}
      <ContextualPanel
        title="Related Transformations"
        items={transformations.map((transformation): ContextualPanelItem => ({
          id: transformation.id,
          title: transformation.name,
          description: transformation.description,
          status: transformation.status,
          metadata: transformation,
          onClick: () => {
            navigate(`/transformations/${transformation.id}`);
            setActivePanel(null);
          },
          actions: [
            {
              label: 'Unlink',
              onClick: () => handleUnlinkTransformation(transformation.id),
              variant: 'danger'
            }
          ]
        }))}
        isLoading={transformationsLoading}
        emptyMessage="No transformations linked to this risk"
        onCreateNew={() => {
          setActivePanel(null);
          openLinkModal();
        }}
        onCreateLabel="Link Transformation"
        isOpen={activePanel === 'transformations'}
        onClose={() => setActivePanel(null)}
      />

      {/* Link Transformation Modal */}
      {showLinkModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowLinkModal(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Link Existing Transformation</h3>
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              {linkModalLoading ? (
                <div className="py-8 text-center text-gray-500">Loading transformations...</div>
              ) : availableTransformations.length === 0 ? (
                <div className="py-6 text-center text-gray-500">
                  <p>No unlinked transformations available.</p>
                  <p className="text-sm mt-2">All transformations are already linked to this risk.</p>
                </div>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select transformation</label>
                  <Select
                    value={selectedTransformationId}
                    onChange={(e) => setSelectedTransformationId(e.target.value)}
                    options={[
                      { value: '', label: '-- Select --' },
                      ...availableTransformations.map((t) => ({
                        value: t.id,
                        label: `${t.name || 'Untitled'}${t.status ? ` (${t.status})` : ''}`
                      }))
                    ]}
                    className="w-full mb-4"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleLinkTransformation} disabled={!selectedTransformationId}>
                      Link
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
